import PartnerList from '@/src/components/PartnerList/PartnerList';
import useCloseFunction from '@/src/hooks/useCloseFunction';
import usePageLoaded from '@/src/hooks/usePageLoaded';
import { createMetricSender } from '@/src/metrics/send-metric';
import QCPartnerList from '@components/QCPartnerList/QCPartnerList';
import Typography from '@pedidosya/web-fenix/foundations/Typography';
import FeedbackPage from '@pedidosya/web-fenix/system/FeedbackPage';
import FooterButton from '@pedidosya/web-fenix/system/FooterButton';
import locationReload from '@utils//navigation/locationReload';
import dynamic from 'next/dynamic';
import { memo, useEffect, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { GLOBAL_HEADERS } from '@/src/utils/constants';
import { HeaderBackgroundV3 } from '@/src/components/HeaderBackground/HeaderBackgroundV3';
import { v4 as uuidv4 } from 'uuid';
import { isVersionGreaterOrEqual } from '@/src/utils/utils';
import getAppVersion from '../../redux/selectors/shared/getAppVersion';
import getCurrentUser from '../../redux/selectors/user/getCurrentUser';
import { logError, setUserContext } from '../../utils/sentry';

interface NavbarDesktopProps {
  onClick?: () => void;
  address?: string;
  passport?: any;
  location?: any;
  country?: any;
  env?: string;
}

interface ItemsGridProps {
  countryId: number;
  landingId: string;
  lat: string;
  lng: string;
  landingType: string;
  userId: number;
  filters: any;
  businessType: string;
  headers: any;
  origin: string;
  landingTitle: string;
  banner: any;
  channelId: string;
  strategyId: string;
  currencyIsoCode: string;
  currencySymbol: string;
  filtersUI: any;
  platform: string;
  countryShortName: string;
  rankingRequestId: string;
  initialItems: any;
  initialTotalItems: number;
  initialSizeItems: number;
  initialOriginalPeyaTrace: string;
  initialMidasRequestId: string;
  isMigration: boolean;
}

const NavbarDesktop = dynamic<NavbarDesktopProps>(() =>
  import('@/src/components/NavbarDesktop/NavbarDesktop').then(
    (mod) => mod.NavbarDesktop,
  ),
);

const ItemsGrid = dynamic<ItemsGridProps>(() =>
  import('@components/ItemsGrid/ItemsGrid').then((mod) => mod.ItemsGrid),
);

const Section = styled.section`
  background-color: white;
  height: 100vh;
  > h3,
  picture {
    text-align: center;
    max-width: 100%;
  }
`;

const FeedBackPageStyle = styled(FeedbackPage)`
  justify-content: center !important;
`;

const ContainerTitleDesktop = styled.div`
  padding-top: 56px;
  padding-left: 16px;
`;

const ContainerLandings = styled.div<{ isDesktop: boolean }>`
  ${({ isDesktop }) =>
    isDesktop &&
    `
    width: 59.4% !important;
    min-width: 864px;
    margin-left: auto;
    margin-right: auto;
  `}
`;

type DetailChannelProps = {
  initialState: any;
  fwfConfig: any;
};

export const DetailChannel = ({
  initialState,
  fwfConfig,
}: DetailChannelProps) => {
  const { closeFn } = useCloseFunction(fwfConfig?.platform);
  const { sendPageLoaded } = usePageLoaded(fwfConfig?.platform);
  const user = useSelector(getCurrentUser);
  const appVersion = useSelector(getAppVersion);
  const isNewVersion = isVersionGreaterOrEqual(appVersion, '10.19.0.0');
  const [showFilters, setShowFilters] = useState(false);
  const [shadowHeader, setShadowHeader] = useState(false);
  const [isScroll, setIsScroll] = useState(false);

  const metricSender = createMetricSender();

  const {
    landing,
    country,
    location,
    location: { address },
    lat: latitude,
    lng: longitude,
    banner,
    origin,
    vendorChain,
    isDesktop,
    headers,
    passport,
    env,
  } = initialState;

  const isMigration = landing?.isMigration;
  const isItemLanding = landing?.value === 'grid' || landing?.value === 'ITEM';
  const isQCLanding = landing?.value === 'qc';
  const rankingRequestId = headers?.[GLOBAL_HEADERS.TRACE_ID] || uuidv4();

  useEffect(() => {
    sendPageLoaded();
  }, [sendPageLoaded]);

  useEffect(() => {
    setUserContext({ user, appVersion });
  }, [appVersion, user]);

  useEffect(() => {
    if (vendorChain && landing?.channel_id) {
      window.location.href = `pedidosya://shop_detail/${vendorChain?.id}?origin=generic-landing&channel_id=${landing?.channel_id}`;
    }
  }, [vendorChain, landing?.channel_id]);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      setIsScroll(scrolled > 0);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (!initialState?.landing) {
    return (
      <FeedBackPageStyle
        description="Tuvimos un problema. Reintente nuevamente."
        title="Hubo un error"
        variant="error"
      >
        <FeedbackPage.FooterButton>
          <FooterButton.Button
            fullWidth
            hierarchy="primary"
            label="Reintentar"
            onClick={() => locationReload()}
            size="large"
          />
        </FeedbackPage.FooterButton>
      </FeedBackPageStyle>
    );
  }

  const rootMargin =
    fwfConfig?.platform === 'ios' ? '-70px 0px 0px 0px' : '-30px 0px 0px 0px';

  const handleInViewChange = (inView) => {
    setShowFilters(!inView);
    setShadowHeader(!shadowHeader);
  };

  let channelId = '';

  if (landing && landing.metadata && landing.metadata.list_filters) {
    for (const filter of landing.metadata.list_filters) {
      const { type, values } = filter || {};

      if (type === 'CHANNEL_ID' && values && values.length > 0) {
        [channelId] = values;
        break;
      }
    }
  }

  const handleListItemsOrVendors = () =>
    isItemLanding ? (
      <ItemsGrid
        countryId={country?.id}
        landingId={landing?.id}
        lat={latitude}
        lng={longitude}
        landingType={landing?.type}
        userId={user?.id}
        filters={landing?.filters}
        headers={headers}
        origin={origin}
        landingTitle={landing?.content_details?.title}
        banner={banner}
        businessType={landing?.businessType}
        channelId={channelId}
        strategyId={landing?.metadata?.strategy_id}
        filtersUI={landing?.content_details?.ui_filters}
        platform={fwfConfig.platform}
        currencySymbol={country?.currencySymbol}
        currencyIsoCode={country?.currencyIsoCode}
        countryShortName={country?.shortName}
        rankingRequestId={rankingRequestId}
        initialItems={landing?.data}
        initialTotalItems={landing?.pagination?.total}
        initialSizeItems={landing?.pagination?.size}
        initialOriginalPeyaTrace={landing?.metadata?.original_peya_trace_id}
        initialMidasRequestId={landing?.metadata?.midas_request_id}
        isMigration={isMigration}
      />
    ) : (
      <PartnerList
        origin={origin}
        landingId={landing?.id}
        filters={landing?.content_details?.filters}
        country={country.id}
        lat={latitude}
        lng={longitude}
        landingTitle={landing?.content_details?.title}
        landingType={landing?.type}
        channelId={channelId}
        currencyIsoCode={country?.currencyIsoCode}
        platform={fwfConfig.platform}
        showFilters={showFilters}
        banner={banner}
        isDesktop={isDesktop}
        headers={headers}
        filtersMetadata={landing?.filters}
        strategyId={landing?.metadata?.strategy_id}
        rankingRequestId={rankingRequestId}
        isNewVersion={isNewVersion}
        data-testid="partner-list"
        initialVendors={landing?.data}
        initialTotalVendors={landing?.pagination?.total}
        initialSize={landing?.pagination?.size}
        initialOriginalPeyaTrace={landing?.metadata?.original_peya_trace_id}
        initialMidasRequestId={landing?.metadata?.midas_request_id}
        isMigration={isMigration}
      />
    );

  const onClickGoBack = () => {
    try {
      metricSender.onBackButtonClick();
      closeFn();
    } catch (error) {
      logError('ERROR_CLICK_BACK_BUTTON_CAMPAING_LANDING', error);
    }
  };

  return (
    <Section>
      {isDesktop && (
        <NavbarDesktop
          address={address}
          passport={passport}
          location={location}
          country={country}
          env={env}
        />
      )}
      <ContainerLandings isDesktop={isDesktop}>
        {!vendorChain && (
          <>
            {!isDesktop ? (
              <InView>
                <HeaderBackgroundV3
                  landing={landing}
                  onClickGoBack={onClickGoBack}
                  fwfConfig={fwfConfig}
                  isScroll={isScroll}
                  isItemLanding={isItemLanding}
                  rootMargin={rootMargin}
                  handleInViewChange={handleInViewChange}
                />
              </InView>
            ) : null}

            {isDesktop && (
              <ContainerTitleDesktop>
                <Typography
                  token="font-headline-highcontrast-large"
                  color="text-color-primary"
                >
                  {landing?.content_details?.title}
                </Typography>
              </ContainerTitleDesktop>
            )}

            {isQCLanding ? (
              <QCPartnerList
                landingId={landing?.id}
                countryId={country?.id}
                latitude={latitude}
                longitude={longitude}
                landingType={landing?.type}
                userId={user?.id}
                filters={landing?.filters}
                headers={headers}
                isDesktop={isDesktop}
                platform={fwfConfig.platform}
                landingTitle={landing?.content_details?.title}
                bannerId={banner?.bannerId}
                businessType={landing?.businessType}
                rankingRequestId={rankingRequestId}
                initialVendors={landing?.data}
                initialTotalVendors={landing?.pagination?.total}
                initialSize={landing?.pagination?.size}
                initialOriginalPeyaTrace={
                  landing?.metadata?.original_peya_trace_id
                }
                initialMidasRequestId={landing?.metadata?.midas_request_id}
                isMigration={isMigration}
              />
            ) : (
              handleListItemsOrVendors()
            )}
          </>
        )}
      </ContainerLandings>
    </Section>
  );
};

export default memo(DetailChannel);
